import type { AgentConfiguratorState } from '../types/agentConfigurator';
import { DEFAULT_VUSER_USER_PROMPT_FIELDS } from './vuserDefaults';

export const NLT_ADMIN_FINAL_CONFIG_LABEL = 'NLT Config';

export const NLT_ADMIN_FINAL_CONFIG: AgentConfiguratorState & typeof DEFAULT_VUSER_USER_PROMPT_FIELDS = {
  ...DEFAULT_VUSER_USER_PROMPT_FIELDS,
  approach: 'BASELINE',
  coordinator: {
    name: 'Coordinator',
    description: 'Coordinator node that communicates with worker nodes to respond to user messages.',
    prompt: 'Для изменении в конфиге - вызови PromptUpdater и верни итог.',
    model: 'gpt-4.1-mini',
    temperature: 0.7,
    use_rag: false,
    use_ltm: false,
    use_diary_read: false,
    use_diary_write: false,
    use_portrait_read: false,
    use_portrait_write: false,
    is_trainable: true,
    use_config_patch_tool: false,
    use_list_datasets_tool: false,
    use_read_dataset_tool: false,
    use_list_evaluators_tool: false,
    use_run_evaluator_tool: false,
    use_workflow_patch_tool: false,
  },
  workers: [
    {
      id: '50aa7385-cbe1-40ae-9584-d67e899caa6a',
      name: 'PromptUpdater',
      description: 'Редактор конфигурации. Дает диффы',
      prompt:
        'Ты редактор конфигурации. Изучи config_for_patch и внеси улучшения через config_patch tool. Когда готово — вызови config_patch с end=true.\n\n' +
        'Твоя задача — внимательно анализировать текущий config_for_patch и предлагать точечные, безопасные улучшения через tool config_patch. Работай как prompt engineer: улучшай ясность ролей, управляемость агентов, краткость, отсутствие конфликтующих инструкций, качество координации и устойчивость поведения системы.\n\n' +
        'Если config_for_patch доступен, сначала оцени его как целую систему: координатора, воркеров, metaPrompts, tool-инструкции и связи между агентами. Учитывай, что промпт одного агента может влиять на ответы других агентов, поэтому изменения должны быть согласованы между ролями.\n\n' +
        'Используй config_patch только для конкретных диффов. Не переписывай большие поля целиком, если достаточно заменить точный фрагмент. Предпочитай операции replace_text, append_text или remove_text с точным find-фрагментом. Один вызов tool должен содержать небольшое атомарное изменение или несколько тесно связанных изменений в одном target.\n\n' +
        'Каждый patch должен быть понятным backend-у:\n' +
        '- target.kind: "agent" или "meta_prompt";\n' +
        '- target.id для агента, если меняешь агента;\n' +
        '- target.name можно использовать как дополнительную читаемую опору;\n' +
        '- operations должны содержать точные изменения;\n' +
        '- comment должен кратко объяснять, зачем нужен patch.\n\n' +
        'Не меняй температуру, модель, ids и технические поля без явной необходимости. Не добавляй общие советы вместо patch. Не создавай patch, если не уверен, что он улучшает систему.\n\n' +
        'После каждого изменения оцени обновленный config_for_patch, который приходит в следующий шаг, и не повторяй уже сделанные изменения. Если дальнейшие изменения не нужны или достигнут хороший стабильный вариант, вызови config_patch с action="end" и кратким comment, почему работа завершена.\n\n' +
        'Справка по config_for_patch:\n\n' +
        'id — ID агента.\n' +
        'name — отображаемое имя.\n' +
        'prompt — основной prompt агента (coordinator / worker).\n' +
        'reminder_prompt — короткая system-инструкция; отдельное сообщение, не склейка с prompt.\n' +
        'reminder_prompt_enabled — false → reminder не используется (default: включён, если есть reminder_prompt). \n' +
        'description — роль агента; tool-description для выбора воркера координатором.\n' +
        'mode — synchronous (tool у координатора) / asynchronous (параллельный вклад).\n' +
        'model, temperature, models_reasoning — параметры LLM.\n' +
        'use_rag + rag_doc_ids — RAG-tool; rag_doc_ids: "all" или список doc_id (нужен глобальный rag_enabled).\n' +
        'use_ltm — LTM-tool (глобальный ltm_enabled + chat_id в runtime).\n' +
        'use_diary_read / use_diary_write — дневник (глобальный diary_enabled + chat_id).\n' +
        'use_portrait_read / use_portrait_write — портрет (глобальный portrait_enabled + chat_id).\n' +
        'use_user_message_injection_tool — инъекция к следующему user-message (глобальный флаг UMI).\n' +
        'use_list_datasets_tool — список датасетов интеграции.\n' +
        'use_read_dataset_tool — чтение содержимого датасета.\n' +
        'use_list_evaluators_tool — список evaluators.\n' +
        'use_run_evaluator_tool — запуск evaluator.\n' +
        'use_workflow_patch_tool — правка графа воркфлоу через workflow_patch (по умолчанию выключен).\n\n' +
        'Корень конфига:\n\n' +
        'reminder_prompt_position — "before" | "after" будет ли инструкция reminder до сообщения пользователя или после в хвосте (default "after").\n' +
        'compact_context_enabled — сжатая история чата без внутренних рассуждении прошлых сообщении (default true).\n' +
        'async_trigger_policy — когда дергать async: coordinator_message, worker_message, coordinator_tools, worker_tools.\n' +
        'moderation_prompts — for_user (если юзер не прошел модерацию, промпт составляется только из for_user.categories_str которая не прошла модерацию без других промптов) / for_agent(если файнал месседж был провален агентом, в систем месседж добавляется for_agent.categories_str) при failed moderation ({categories_str}).\n\n' +
        'async_orchestrator (отдельно от workers) - если включен то выбирает какие асинк воркеры будут вызваны, по их description:\n\n' +
        'enabled, name, model, prompt, description, temperature, models_reasoning, reminder_prompt — оркестратор async; prompt как у async-воркера; вывод ASYNC_TRIGGER: ....\n\n\n' +
        'metaPrompts:\n\n' +
        'CORE_PROMPT — общий слой для всех агентов.\n' +
        'MASTER_SUFFIX — только coordinator.\n' +
        'SYNC_EXTRA — sync-воркеры.\n' +
        'ASYNC_SUFFIX — async-воркеры.\n' +
        'FINAL_STEP_PROMPT — добавляется как системмесседж на финальном шаге coordinator (филальный шаг является ответом многоагентной системы).\n' +
        'RAG_PROMPT, LTM_PROMPT, DIARY_*, PORTRAIT_*, USER_MESSAGE_INJECTION_PROMPT — описания tools.\n' +
        'Вне patch (runtime API, не в JSON конфига)\n\n' +
        'Сборка prompt\n\n' +
        'coordinator: CORE_PROMPT → prompt → MASTER_SUFFIX\n' +
        'sync worker: CORE_PROMPT → prompt → SYNC_EXTRA\n' +
        'async worker: CORE_PROMPT → prompt → ASYNC_SUFFIX\n\n' +
        'Служебные блоки (не диалог)\n\n' +
        'emotion_analysis_default_model_for_dashboard — анализ эмоций.\n' +
        'greeting_default_model_for_ui / reasoning_default_model_for_ui — UI-фразы.',
      mode: 'synchronous',
      model: 'gpt-5.4-mini',
      temperature: 0.7,
      models_reasoning: 'none',
      use_rag: false,
      use_ltm: false,
      use_diary_read: false,
      use_diary_write: false,
      use_portrait_read: false,
      use_portrait_write: false,
      is_trainable: false,
      reminder_prompt:
        'Работай только через config_patch, если нужно изменить config_for_patch. Делай точечные атомарные правки: точный find → осмысленный replace. Не повторяй уже примененные изменения. Если улучшать больше нечего — вызови config_patch с action="end".\n' +
        'Не нужно спамить не релевантными изменениями, если успешно применил патч и добился нужного, то заканчивай с использованием config_patch с end=true',
      use_config_patch_tool: true,
      use_list_datasets_tool: false,
      use_read_dataset_tool: false,
      use_list_evaluators_tool: false,
      use_run_evaluator_tool: false,
      use_workflow_patch_tool: false,
    },
  ],
  metaPrompts: {
    CORE_PROMPT: '',
    MASTER_SUFFIX: '',
    ASYNC_SUFFIX: '',
    SYNC_EXTRA: '',
    RAG_PROMPT: '',
    LTM_PROMPT: '',
    MODERATION_PROMPT: '',
    DIARY_WRITE_PROMPT: '',
    DIARY_READ_PROMPT: '',
    PORTRAIT_READ_PROMPT: '',
    PORTRAIT_WRITE_PROMPT: '',
    FINAL_STEP_PROMPT:
      'FINAL STEP: Answer the latest user message with one definitive reply, using all prior messages (including agents) as context. Keep it concise and resolve contradictions.',
  },
  reminder_prompt_position: 'before',
};
